import styled from "styled-components";

const bp = { sm: "560px", md: "768px", lg: "1100px" };

const c = {
  ink: "#171717",
  inkSoft: "#5f5a57",
  white: "#ffffff",
  cream: "#fffdfa",
  gold: "#c9a227",
  goldDark: "#b88f1d",
  goldLine: "rgba(201,162,39,0.18)",
  pink: "#f6d6e4",
  pinkStrong: "#e88ab2",
  pinkDeep: "#d95e97",
  pinkSoft: "rgba(232,138,178,0.08)",
  pinkBorder: "rgba(232,138,178,0.18)",
  border: "rgba(23,23,23,0.08)",
  borderStrong: "rgba(23,23,23,0.11)",
  shadowSoft: "0 8px 22px rgba(0,0,0,0.04)",
  shadowCard: "0 16px 34px rgba(0,0,0,0.05)",
  darkBtn: "#111111",
  error: "#bf5c5c",
};

export const Page = styled.section`
  margin-top: 100px;
  min-height: 100vh;
  padding: 36px 16px 90px;
  background: transparent;

  @media (max-width: ${bp.md}) {
    padding: 10px 12px 72px;
  }
`;

export const Inner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

export const Title = styled.h1`
  margin: 20px 0 8px;
  font-size: clamp(36px, 6vw, 72px);
  font-family: "Cormorant Garamond", serif;
  line-height: 0.95;
  font-weight: 500;
  color: ${c.ink};
  letter-spacing: 0.01em;

  @media (max-width: ${bp.md}) {
    margin-top: 0;
    font-size: 42px;
  }
`;

export const Subtitle = styled.p`
  max-width: 720px;
  margin: 0 0 18px;
  font-size: 15px;
  line-height: 1.65;
  color: ${c.inkSoft};

  @media (max-width: ${bp.md}) {
    margin-bottom: 14px;
    font-size: 13.5px;
  }
`;

export const EmptyBox = styled.div`
  border: 1px solid ${c.borderStrong};
  background: linear-gradient(180deg, #ffffff 0%, #fffdfa 100%);
  padding: 24px;
  font-size: 18px;
  color: ${c.inkSoft};
  border-radius: 22px;
  box-shadow: ${c.shadowSoft};

  @media (max-width: ${bp.md}) {
    padding: 18px 14px;
    border-radius: 16px;
    font-size: 16px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(300px, 0.78fr);
  gap: 16px;
  align-items: start;

  @media (max-width: ${bp.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const List = styled.div`
  display: grid;
  gap: 10px;
`;

export const ItemCard = styled.article`
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 190px;
  gap: 12px;
  align-items: stretch;
  padding: 10px 12px;
  border-radius: 18px;
  border: 1px solid ${c.borderStrong};
  background: linear-gradient(180deg, #ffffff 0%, #fffdf9 100%);
  box-shadow: ${c.shadowSoft};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 18px;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px rgba(201, 162, 39, 0.04);
  }

  @media (max-width: ${bp.md}) {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 16px;

    &::before {
      border-radius: 16px;
    }
  }
`;

export const ItemTop = styled.div`
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  min-width: 0;

  @media (max-width: ${bp.md}) {
    grid-template-columns: 62px minmax(0, 1fr);
    gap: 10px;
  }
`;

export const Img = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 14px;
  border: 1px solid rgba(201, 162, 39, 0.12);
  background: #fff;

  @media (max-width: ${bp.md}) {
    width: 62px;
    height: 62px;
    border-radius: 12px;
  }
`;

export const Info = styled.div`
  min-width: 0;
  display: grid;
  gap: 5px;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
`;

export const HeaderTools = styled.div`
  display: none;
  flex-shrink: 0;

  @media (max-width: ${bp.md}) {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
  }
`;

export const Name = styled.h3`
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
  font-size: 21px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  line-height: 1.05;
  color: ${c.ink};
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: ${bp.md}) {
    font-size: 18px;
    line-height: 1.08;
    padding-right: 2px;
  }
`;

export const Meta = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #7a664c;
  line-height: 1.35;
`;

export const MetaAlert = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${c.error};
  line-height: 1.35;
  font-weight: 700;
`;

export const InlineControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const GramajSelect = styled.select`
  width: 100%;
  max-width: 210px;
  min-height: 38px;
  border: 1px solid ${c.pinkBorder};
  border-radius: 12px;
  background: #fff;
  padding: 0 10px;
  font-size: 13px;
  color: ${c.ink};
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }

  @media (max-width: ${bp.md}) {
    max-width: 100%;
  }
`;

export const SelectField = styled.select`
  width: 100%;
  max-width: 380px;
  min-height: 38px;
  border: 1px solid rgba(201, 162, 39, 0.18);
  border-radius: 12px;
  background: #fff;
  padding: 0 10px;
  font-size: 13px;
  color: ${c.ink};
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }

  @media (max-width: ${bp.md}) {
    max-width: 100%;
  }
`;

export const Actions = styled.div`
  min-width: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
  padding-left: 6px;

  @media (max-width: ${bp.md}) {
    border-top: 1px solid rgba(23, 23, 23, 0.05);
    padding-top: 6px;
    padding-left: 0;
    gap: 6px;
    align-items: stretch;
  }
`;

export const TopActions = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: ${bp.md}) {
    display: none;
  }
`;

export const BottomRow = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 12px;
  margin-top: auto;

  @media (max-width: ${bp.md}) {
    align-items: center;
    justify-content: space-between;
  }
`;

export const QtyWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(17, 17, 17, 0.03);
  border: 1px solid rgba(17, 17, 17, 0.07);
  border-radius: 999px;
  padding: 3px;

  @media (max-width: ${bp.md}) {
    gap: 4px;
  }
`;

export const MobileQtyWrap = styled(QtyWrap)`
  display: none;

  @media (max-width: ${bp.md}) {
    display: inline-flex;
  }
`;

export const QtyBtn = styled.button`
  width: 27px;
  height: 27px;
  border: 0;
  border-radius: 999px;
  background: ${c.darkBtn};
  color: #fff;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.15s ease,
    background 0.15s ease;

  &:hover {
    background: ${c.gold};
  }

  &:active {
    transform: scale(0.96);
  }

  @media (max-width: ${bp.md}) {
    width: 26px;
    height: 26px;
    font-size: 14px;
  }
`;

export const QtyValue = styled.div`
  min-width: 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  color: ${c.ink};
`;

export const RemoveIconBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fff;
  color: #8d8d8d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;
  flex-shrink: 0;

  svg {
    font-size: 14px;
  }

  &:hover {
    color: ${c.pinkDeep};
    border-color: rgba(232, 138, 178, 0.22);
    background: rgba(232, 138, 178, 0.04);
  }

  @media (max-width: ${bp.md}) {
    width: 30px;
    height: 30px;

    svg {
      font-size: 12px;
    }
  }
`;

export const PriceWrap = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  text-align: right;
  margin-left: auto;
`;

export const OldPrice = styled.div`
  font-size: 11px;
  color: rgba(23, 23, 23, 0.42);
  text-decoration: line-through;
  white-space: nowrap;
  font-weight: 600;
`;

export const Price = styled.div`
  font-size: 19px;
  color: ${c.pinkDeep};
  white-space: nowrap;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;

  @media (max-width: ${bp.md}) {
    font-size: 17px;
  }
`;

export const DiscountBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 3;
  min-width: 42px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${c.pinkDeep};
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  box-shadow:
    0 8px 18px rgba(232, 138, 178, 0.14),
    0 0 0 3px rgba(255, 255, 255, 0.92);
`;

export const CustomSummaryBox = styled.div`
  display: grid;
  gap: 6px;
  padding: 9px 10px;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: linear-gradient(
    180deg,
    rgba(201, 162, 39, 0.03) 0%,
    rgba(232, 138, 178, 0.04) 100%
  );
`;

export const SummaryLine = styled.div`
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 8px;
  align-items: start;

  @media (max-width: ${bp.md}) {
    grid-template-columns: 72px minmax(0, 1fr);
  }
`;

export const SummaryKey = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: ${c.gold};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

export const SummaryValue = styled.div`
  font-size: 12.5px;
  line-height: 1.4;
  color: ${c.ink};
  word-break: break-word;
`;

export const EditToggleBtn = styled.button`
  width: fit-content;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(201, 162, 39, 0.18);
  background: #fff;
  color: ${c.ink};
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: rgba(232, 138, 178, 0.22);
    background: rgba(232, 138, 178, 0.04);
    color: ${c.pinkDeep};
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(201, 162, 39, 0.22),
    transparent
  );
`;

export const ExtraFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;

  @media (max-width: ${bp.md}) {
    grid-template-columns: 1fr;
  }
`;

export const FieldGroup = styled.div`
  display: grid;
  gap: 6px;
  min-width: 0;
`;

export const FieldGroupWide = styled(FieldGroup)`
  grid-column: 1 / -1;
`;

export const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 800;
  color: ${c.inkSoft};
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 78px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  background: #fff;
  padding: 10px 12px;
  font-size: 13px;
  color: ${c.ink};
  resize: vertical;
  outline: none;

  &:focus {
    border-color: rgba(232, 138, 178, 0.34);
    box-shadow: 0 0 0 4px ${c.pinkSoft};
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FileUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const FileTrigger = styled.label`
  min-width: 118px;
  height: 36px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1px solid rgba(201, 162, 39, 0.2);
  background: linear-gradient(180deg, #fffdf8 0%, #fff8f1 100%);
  color: ${c.ink};
  font-size: 12.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: rgba(232, 138, 178, 0.2);
    background: rgba(232, 138, 178, 0.04);
  }
`;

export const FileName = styled.span`
  font-size: 12.5px;
  color: ${c.inkSoft};
`;

export const FileHint = styled.a`
  font-size: 12px;
  color: ${c.pinkDeep};
  text-decoration: none;
  font-weight: 700;

  &:hover {
    text-decoration: underline;
  }
`;

export const Summary = styled.aside`
  position: sticky;
  top: 132px;
  align-self: start;
  display: grid;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(232, 138, 178, 0.14);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 251, 253, 0.98) 100%
  );
  box-shadow: ${c.shadowCard};

  &::before {
    content: "";
    display: block;
    height: 1px;
    margin-bottom: 2px;
    background: linear-gradient(90deg, transparent, ${c.goldLine}, transparent);
  }

  @media (max-width: ${bp.lg}) {
    position: static;
  }

  @media (max-width: ${bp.md}) {
    padding: 15px;
    border-radius: 18px;
  }
`;

export const SummaryTitle = styled.h2`
  margin: 0;
  font-size: 30px;
  font-family: "Cormorant Garamond", serif;
  font-weight: 650;
  color: ${c.ink};

  @media (max-width: ${bp.md}) {
    font-size: 24px;
  }
`;

export const SummaryText = styled.p`
  margin: -6px 0 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: ${c.inkSoft};
`;

export const SummaryCard = styled.div`
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  background: linear-gradient(
    180deg,
    rgba(201, 162, 39, 0.03) 0%,
    rgba(232, 138, 178, 0.04) 100%
  );
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 16px;
  color: ${c.inkSoft};

  strong {
    color: ${c.ink};
    font-size: 30px;
    font-weight: 800;
    line-height: 1;
  }

  @media (max-width: ${bp.md}) {
    font-size: 15px;

    strong {
      font-size: 24px;
    }
  }
`;

export const SummaryMini = styled.div`
  font-size: 13px;
  color: ${c.inkSoft};
`;

export const PrimaryBtn = styled.button`
  height: 50px;
  border: 0;
  border-radius: 15px;
  background: linear-gradient(180deg, ${c.gold} 0%, ${c.goldDark} 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(183, 140, 26, 0.16);
  transition:
    filter 0.15s ease,
    transform 0.15s ease;

  &:hover {
    filter: brightness(1.03);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SecondaryBtn = styled.button`
  height: 48px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 15px;
  background: #fff;
  color: ${c.ink};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    border-color: rgba(232, 138, 178, 0.22);
    background: rgba(232, 138, 178, 0.04);
    color: ${c.pinkDeep};
  }
`;
